import { useRouter } from 'next/router';
import { FC } from 'react';
import {
  MdOutlineAccountBox,
  MdOutlineFavoriteBorder,
  MdOutlineShoppingCart,
} from 'react-icons/md';

import { Navigation, SearchField } from 'components';
import { useComponentVisible } from 'utils/hooks';
import { AppHeaderProps, Styled } from '.';

import { IconButton } from 'components/core';
import { useI18n } from 'contexts/i18n';
import { useRetriveCart, useRetriveIteroProposal } from 'reactQuery/queries';
import { LoadingSpinner } from 'styles/sharedStyles';
import { isIMSEnabled } from 'utils';
import { useSubscriptionProduct } from 'reactQuery/queries/subscriptions/useSubscriptionProducts';

const AppHeader: FC<AppHeaderProps> = () => {
  const i18n = useI18n();

  const { data: currentCart, isLoading, isFetching } = useRetriveCart();
  const { data: subscriptionProducts } = useSubscriptionProduct();

  const {
    data: proposalData,
    isLoading: isProposalDataLoading,
    isFetching: isProposalDataFetching,
    isIdle: isProposalDataIdle,
  } = useRetriveIteroProposal();

  const isSubscriptionEnabled =
    subscriptionProducts && subscriptionProducts?.length > 0;

  const {
    ref: accountRef,
    isComponentVisible: showAccountMenu,
    setIsComponentVisible: setShowAccountMenu,
  } = useComponentVisible(false);
  const router = useRouter();

  const redirectToCart = () => {
    router.push('/cartview?showcart=1');
  };

  const redirectToFavourites = () => {
    router.push('/favouriteProducts');
  };

  const redirectToTargetPage = () => {
    if (proposalData && proposalData.iteroProposals.length === 1) {
      router.push(
        '/iteroscanners/proposals/' +
          proposalData.iteroProposals[0].proposalName +
          '?showproposal=1'
      );
    } else if (proposalData && proposalData.iteroProposals.length > 1) {
      router.push('/iteroscanners/proposals/list');
    } else {
      router.push('/iteroscanners/scanners');
    }
  };

  const onMenuItemClick = (link: string) => {
    router.push(link);
    setShowAccountMenu(false);
  };

  const orderHistoryPageTitle = isIMSEnabled()
    ? i18n['Orders and Downloads']
    : i18n['Order History'];

  const accountMenuList = [
    { title: i18n['Profile'], url: '/profile' },
    { title: orderHistoryPageTitle, url: '/orderhistorylog' },
    // { title: 'Recurring Orders', url: '/recurringorders' },
    // { title: 'Custom Designs', url: '/' },
    // { title: 'Settings', url: '/settings' },
    // { title: 'Returns', url: '/returns' },
  ];

  if (isSubscriptionEnabled) {
    accountMenuList.push({
      title: i18n['Subscriptions History'],
      url: '/subscribe-and-save?selected=mysubscriptions',
    });
  }

  if (isIMSEnabled()) {
    accountMenuList.push({ title: i18n['Drafts'], url: '/drafts' });
  }

  return (
    <Styled.AppHeaderWrapper>
      <Styled.AppHeaderContainer>
        <Styled.NavBarWrapper>
          <Navigation />
        </Styled.NavBarWrapper>

        <Styled.UserActions>
          <SearchField />

          <Styled.IconsContainer>
            <div ref={accountRef}>
              <IconButton
                onClick={() => setShowAccountMenu(!showAccountMenu)}
                size="md"
                showBorder={true}
              >
                <MdOutlineAccountBox />
              </IconButton>
              {showAccountMenu && (
                <Styled.Menu bottom="-5px">
                  {accountMenuList.map(item => (
                    <Styled.MenuItem
                      key={item.title}
                      onClick={() => onMenuItemClick(item.url)}
                    >
                      {item.title}
                    </Styled.MenuItem>
                  ))}
                </Styled.Menu>
              )}
            </div>

            <Styled.IconButtonWrapper>
              <IconButton
                onClick={redirectToFavourites}
                size="md"
                showBorder={true}
              >
                <MdOutlineFavoriteBorder />
              </IconButton>
            </Styled.IconButtonWrapper>

            <Styled.IconButtonWrapper>
              {!isLoading && (
                <Styled.NotificationBadge>
                  {currentCart?.itemCount}
                </Styled.NotificationBadge>
              )}
              {(isLoading || isFetching) && (
                <Styled.NotificationBadge>
                  <LoadingSpinner />
                </Styled.NotificationBadge>
              )}

              <IconButton onClick={redirectToCart} size="md" showBorder={true}>
                <MdOutlineShoppingCart width={'24px'} height={'24px'} />
              </IconButton>
            </Styled.IconButtonWrapper>

            {proposalData &&
              proposalData.iteroProposals &&
              proposalData?.iteroProposals?.length > 0 && (
                <Styled.IconButtonWrapper>
                  {!isProposalDataLoading && (
                    <Styled.NotificationBadge>
                      {/* {currentIteroCart?.itemCount} */}
                      {proposalData?.iteroProposals?.length}
                    </Styled.NotificationBadge>
                  )}
                  {(isProposalDataLoading || isProposalDataFetching) && (
                    <Styled.NotificationBadge>
                      <LoadingSpinner />
                    </Styled.NotificationBadge>
                  )}

                  <IconButton
                    size="md"
                    showBorder={true}
                    onClick={redirectToTargetPage}
                  >
                    <Styled.IconImage
                      src="/images/icons/contract-icon.svg"
                      alt="itero-contract"
                    />
                  </IconButton>
                </Styled.IconButtonWrapper>
              )}
          </Styled.IconsContainer>
        </Styled.UserActions>
      </Styled.AppHeaderContainer>
    </Styled.AppHeaderWrapper>
  );
};

export default AppHeader;
